import { useEffect, useRef } from 'react';

import { useAtomValue } from 'jotai';

import { useSocialInboxNavigation } from '@/components/pages/conversations/SocialInbox/hooks/useSocialInboxNavigation';

import { classNames } from '@/utils';

import { DMConversation } from '@/components/pages/conversations/SocialInbox/types';

import { selectedMessagesAtom } from '@/components/pages/conversations/SocialInbox/atoms';

import { MarkAsReadUnread } from '@/components/pages/conversations/SocialInbox/components/SocialInboxMessageList/SocialInboxHeader/SocialInboxActions/DirectMessageActions/MarkAsReadUnread';
import { SelectMessageAction } from '@/components/pages/conversations/SocialInbox/components/SocialInboxMessageList/SocialInboxMessage/SocialInboxDirectMessageActions/SelectMessageAction';

type SocialInboxDirectMessageActionsProps = {
    message: DMConversation;
};

export const SocialInboxDirectMessageActions = ({
    message
}: SocialInboxDirectMessageActionsProps) => {
    const selectedMessages = useAtomValue(selectedMessagesAtom);
    const { currentSocialInbox } = useSocialInboxNavigation();
    const isSelected = selectedMessages[currentSocialInbox]?.includes(
        message.meta_conversation_id.toString()
    );
    const divRef = useRef<HTMLDivElement>(null);

    const updateGradient = () => {
        if (!divRef.current) return;
        const isDarkMode = document.documentElement.classList.contains('dark');

        if (isDarkMode) {
            divRef.current.style.background =
                'linear-gradient(90deg, rgba(18, 18, 18, 0.00) 0%, #090A0D 14.71%)';
        } else {
            divRef.current.style.background =
                'linear-gradient(90deg, rgba(255, 255, 255, 0.00) 0%, #FFF 14.71%)';
        }
    };

    useEffect(() => {
        // Set initial gradient
        updateGradient();

        const observer = new MutationObserver(updateGradient);
        observer.observe(document.documentElement, {
            attributes: true,
            attributeFilter: ['class']
        });

        return () => observer.disconnect();
    }, []);

    const isRead = !!message.seen_at;

    return (
        <div
            ref={divRef}
            data-testid="social-inbox-direct-message-actions"
            className={classNames(
                'absolute right-0.5 top-0 flex gap-3 bg-white pl-3 pt-0.5 text-xs transition-opacity duration-200',
                isSelected ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
            )}>
            <MarkAsReadUnread isRead={isRead} message={message} />
            <SelectMessageAction message={message} />
        </div>
    );
};
